import React from "react";

import {
    Button,
    ButtonGroup,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    Fabric,
    IDialogContentProps,
    IDropdownOption,
    IModalProps,
    initializeIcons,
    mergeStyleSets,
    Spinner,
    SpinnerSize,
    TextField,
    Theme,
} from "@nuorder/components";
import { AlertNotification } from "components/alert/alert-notification";
import { validateEmail } from "components/utils/validations";
import { DATATEST } from "constants/integration";
import { SeverityMessages } from "constants/product-collection";
import { useFetchMerchantBrandPortals, useInfiniteFetchMembersInMerchant } from "containers/folders/hooks/merchants";
import { fetchMoreDelayed } from "containers/folders/hooks/utils";
import { ItemKind } from "containers/folders/types/base";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { dateFormat } from "helpers/dates";
import { ISchemaKey } from "helpers/product-collection";
import { formatMessage } from "lib/globalize-util";
import isEqual from "lodash/isEqual";
import me from "models/me";
import moment from "moment";
import { MarketSettingsConfirmationModal } from "./market-settings-confirmation-dialog";
import { MarketSettingsDates } from "./market-settings-dates";
import { MarketSettingsDepartment } from "./market-settings-department";
import {
    AssortmentMarketStatus,
    ErrorMessages,
    MarketSettingsData,
    SaveMarketSettingsEvent,
} from "./market-settings.types";

interface IOwnProps {
    open: boolean;
    readonly?: boolean;
    driveItemData: nuorder.folder.v1.Item;
    onSaveSettings: SaveMarketSettingsEvent;
    onClose: () => void;
    rawItemDisplayProperty: ISchemaKey[];
    merchantId: string;
    collectionItemMerchantId: string;
}

initializeIcons();

const classes = mergeStyleSets({
    container: {
        minHeight: 120,
    },
    brandContact: {
        marginBottom: 8,
    },
});

const styles = {
    textField: {
        fieldGroup: {
            border: "none",
        },
    },
};

export const parseDate = (date?: string): Date =>
    !date ? null : moment(date, dateFormat.YEAR_MONTH_DAY, true).toDate();

export const MarketSettingsModal: React.FC<IOwnProps> = ({
    open,
    readonly,
    driveItemData,
    onSaveSettings,
    onClose,
    rawItemDisplayProperty,
    merchantId,
    collectionItemMerchantId,
}) => {
    const [saveError, setSaveError] = React.useState<string>("");
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [initialMarketSettings, setInitialMarketSettings] = React.useState<MarketSettingsData>({
        assortmentName: nameExtRemover(driveItemData?.name, driveItemData.kind as ItemKind),
        departmentId: driveItemData?.collection_facet?.department_id,
        marketDate: parseDate(driveItemData?.collection_facet?.market_date),
        dueDate: parseDate(driveItemData?.collection_facet?.due_date),
        brandContactEmail: driveItemData?.collection_facet?.brand_contact_email,
        brandContactName: driveItemData?.collection_facet?.brand_contact_name,
        marketStatus:
            (driveItemData?.collection_facet?.market_status as AssortmentMarketStatus) ||
            AssortmentMarketStatus.PENDING_CONFIRMATION,
    });
    const [assortmentSettings, setMarketSettings] = React.useState<MarketSettingsData>({ ...initialMarketSettings });
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [isValidBrandContactEmail, setIsInvalidBrandContactEmail] = React.useState<boolean>(false);

    const getBrandPortalId = (item: nuorder.folder.v1.Item) => {
        let brandPortalId = undefined;
        if (item?.kind === ItemKind.CollectionDocument && item?.collection_facet?.brand_portal_id?.length) {
            brandPortalId = item?.collection_facet?.brand_portal_id;
        }
        return brandPortalId;
    };
    const externalMarketbrandPortalId = getBrandPortalId(driveItemData);
    const { data: externalBrandPortal } = useFetchMerchantBrandPortals(externalMarketbrandPortalId, {
        enabled: !!driveItemData && open && externalMarketbrandPortalId,
    });

    const {
        data: merchantData,
        isFetching: merchantIsFetching,
        isFetchingMore: merchantIsFetchingMore,
        fetchMore: merchantFetchMore,
        canFetchMore: merchantCanFetchMore,
    } = useInfiniteFetchMembersInMerchant(
        externalBrandPortal?.merchant_id,
        {},
        { enabled: !!externalBrandPortal?.merchant_id },
    );

    React.useEffect(() => {
        fetchMoreDelayed(merchantCanFetchMore, merchantIsFetchingMore, merchantIsFetching, merchantFetchMore);
    }, [merchantCanFetchMore, merchantIsFetchingMore, merchantIsFetching, merchantFetchMore, merchantData]);

    const handleCloseModal = React.useCallback(() => {
        const noChanges = isEqual(assortmentSettings, initialMarketSettings);

        if (noChanges) {
            onClose();
        } else {
            setShowConfirmationDialog(true);
        }
    }, [assortmentSettings, initialMarketSettings, onClose]);

    const handleCloseConfirmation = React.useCallback(() => {
        setShowConfirmationDialog(false);
    }, [setShowConfirmationDialog]);

    const handleSaveSettings = React.useCallback(() => {
        setIsSaving(true);
        onSaveSettings(assortmentSettings, (error) => {
            setIsSaving(false);
            setInitialMarketSettings(assortmentSettings);
            if (!error) {
                onClose();
            } else {
                setSaveError(error);
            }
        });
    }, [onSaveSettings, onClose, assortmentSettings]);

    const { valid, errors } = React.useMemo(() => {
        let valid = true;
        const errors: ErrorMessages<MarketSettingsData> = {};

        if (!assortmentSettings.assortmentName) {
            valid = false;
            errors.assortmentName = formatMessage("Market name cannot be blank");
        }

        return {
            valid,
            errors,
        };
    }, [assortmentSettings]);

    const errorMessage = React.useMemo(() => {
        return errors?.assortmentName || errors?.brandContactEmail || saveError;
    }, [errors, saveError]);

    const isSaveDisabled = React.useMemo(() => {
        const noChanges = isEqual(assortmentSettings, initialMarketSettings);
        const disabled = readonly || !valid || noChanges || isValidBrandContactEmail;
        return saveError && noChanges ? disabled || !!saveError : disabled;
    }, [assortmentSettings, initialMarketSettings, readonly, valid, isValidBrandContactEmail, saveError]);

    const handleMarketStatus = React.useCallback(
        (_value, option: IDropdownOption) => {
            setMarketSettings((prev) => ({
                ...prev,
                marketStatus: option.key as AssortmentMarketStatus,
            }));
        },
        [setMarketSettings],
    );

    const handleSelectedBrandContact = React.useCallback(
        (_e, newBrandContact) => {
            if (validateEmail(newBrandContact)) {
                setIsInvalidBrandContactEmail(false);
            }

            setMarketSettings((prev) => ({
                ...prev,
                brandContactEmail: newBrandContact || "",
                brandContactName: "",
            }));
        },
        [setMarketSettings],
    );

    const marketStatusOptions: IDropdownOption[] = React.useMemo(
        () =>
            Object.keys(AssortmentMarketStatus).map((template) => ({
                id: template,
                key: template,
                text: formatMessage(AssortmentMarketStatus[template]),
            })),
        [],
    );

    const dialogContentProps = React.useMemo<IDialogContentProps>(
        () => ({
            type: DialogType.normal,
            closeButtonAriaLabel: formatMessage("Close"),
            showCloseButton: true,
            titleProps: {
                "data-test": DATATEST.FOLDERS.DIALOG.TITLE,
            } as any,
            title: formatMessage("Market Settings"),
            styles: {
                innerContent: {
                    overflow: "visible",
                },
            },
        }),
        [],
    );

    const modalProps = React.useMemo<IModalProps>(
        () => ({
            titleAriaId: "dialogLabel",
            subtitleAriaId: "subTextLabel",
            isBlocking: true,
            styles: {
                main: { width: "600px !important", maxWidth: "100vw !important" },
            },
        }),
        [],
    );

    const handleOnBlurBrandContact = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const brandContactEmail = event.target.value;
        if (!!brandContactEmail && !validateEmail(brandContactEmail)) {
            setIsInvalidBrandContactEmail(true);
        } else {
            setIsInvalidBrandContactEmail(false);
        }
    };

    return (
        <Theme>
            <Fabric>
                <Dialog
                    hidden={!open || showConfirmationDialog}
                    onDismiss={handleCloseModal}
                    dialogContentProps={dialogContentProps}
                    modalProps={modalProps}
                >
                    {errorMessage && (
                        <AlertNotification
                            message={errorMessage}
                            severity={SeverityMessages.Error}
                            open={true}
                            noMargin
                            dataTest={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.ALERT_MESSAGE}
                        />
                    )}
                    <div className={classes.container}>
                        <MarketSettingsDepartment
                            merchantId={me.isBrandUser() ? collectionItemMerchantId : merchantId}
                            assortmentSettings={assortmentSettings}
                            rawItemDisplayProperty={rawItemDisplayProperty}
                            setAssortmentSettings={setMarketSettings}
                            readonly={readonly}
                        />
                        <MarketSettingsDates
                            readonly={readonly}
                            assormentSettings={assortmentSettings}
                            setAssormentSettings={setMarketSettings}
                        />
                        <TextField
                            data-test={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.BRAND_CONTACT}
                            value={assortmentSettings.brandContactEmail}
                            onChange={handleSelectedBrandContact}
                            onBlur={handleOnBlurBrandContact}
                            type="email"
                            errorMessage={
                                isValidBrandContactEmail && formatMessage("Please enter a valid email address")
                            }
                            label={formatMessage("Brand Contact")}
                            placeholder="name@domain.com"
                            disabled={readonly}
                            styles={styles.textField}
                        />
                        <Dropdown
                            testId={DATATEST.ASSORTMENTS.ASSORMENT_SETTINGS.MARKET_STATUS}
                            selectedKey={assortmentSettings.marketStatus}
                            options={marketStatusOptions}
                            disabled={readonly}
                            onChange={handleMarketStatus}
                            label={formatMessage("Market Status")}
                        />
                    </div>
                    <DialogFooter>
                        <ButtonGroup horizontalAlign={"end"}>
                            <Button
                                data-test={DATATEST.ASSORTMENTS.DIALOG.CANCEL}
                                disabled={isSaving}
                                onClick={handleCloseModal}
                                text={formatMessage("Cancel")}
                            />
                            <Button
                                primary={true}
                                data-test={DATATEST.ASSORTMENTS.DIALOG.SAVE}
                                onClick={handleSaveSettings}
                                disabled={isSaveDisabled || isSaving}
                                text={formatMessage("Save")}
                            >
                                {isSaving && <Spinner size={SpinnerSize.small} />}
                            </Button>
                        </ButtonGroup>
                    </DialogFooter>
                </Dialog>
                {showConfirmationDialog && (
                    <MarketSettingsConfirmationModal
                        onClose={handleCloseConfirmation}
                        open={true}
                        onConfirm={onClose}
                    />
                )}
            </Fabric>
        </Theme>
    );
};
